import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import { SubHeaderService } from "../services/sub-header.service";

import { SubHeader } from "../models/sub-header.model";
import { AuthService } from "../../services/auth.service";
import { TimeoutConfig } from "../../services/auth.config";
import { SidePanelService } from "../../common/services/side-panel.service"
import { SidePanel } from "../../common/models/side-panel.model";
import { StateType } from "../../common/models/flow.dto";
import { UserIdentity } from "../../models/user-identity.model";
import { DynamicDialogService } from "../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../core/models/dialog.model";
import { ApplicationConfigService, SystemAlert } from "../../services/application-config.service";

@Component({
    selector: "home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {

    @ViewChild("login") public loginLink: ElementRef;
    @ViewChild("register") public registerLink: ElementRef;

    public alerts: SystemAlert[] = [];

    _anonymousSet: boolean = false;
    get anonymousSet(): boolean {
        return this._anonymousSet;
    }

    _config: TimeoutConfig;

    get config(): TimeoutConfig {
        if (this._config == null) return null;
        return this._config;
    }

    get cookiesAccepted(): boolean {
        let value = this.authService.cookieAcceptance;
        return value;
    }

    cookieDialogText: string = "RICS uses cookies strictly to maintain user identity and session.  No marketing or other data is stored in a cookie beyond what is required to maintain the integrity of your user session.";

    cookieDialog: Dialog = new Dialog(this.cookieDialogText, [new DialogButton("Accept", true, () => this.acceptCookie(true)), new DialogButton("Decline", false, () => this.acceptCookie(false))]);


    newUserCookieDialog: Dialog = new Dialog(this.cookieDialogText, [new DialogButton("Accept", true, () => this.acceptCookieAndRegister(true)), new DialogButton("Decline", false, () => this.acceptCookieAndRegister(false))]);


    doNavigate: boolean = false;
    _loggedIn: boolean = false;
    
    get loggedInUser(): boolean {
        return this._loggedIn; 
    }

    notFound: boolean = false;

    sidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Field Notes", stateTypeRequired: StateType.ReviewForm }, { title: "Review Notes", stateTypeRequired: StateType.ReviewForm }, { title: "History", stateTypeRequired: StateType.ReviewForm }],
        sections: [{ componentName: 'FieldNotes' }, { componentName: 'ReviewNotes' }, { componentName: 'History' }]
    });

    subHeader: SubHeader = new SubHeader({ title: 'Home' });
    subscription: Subscription = new Subscription();
   
    constructor(public subHeaderService: SubHeaderService, private route: ActivatedRoute, public router: Router, public client: HttpClient,
        public sidePanelService: SidePanelService, public authService: AuthService, private dialogService: DynamicDialogService, private configService: ApplicationConfigService
    ) {
        subHeaderService.updateSubHeader(new SubHeader({ title: "Home" }));
    }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader); 
        this._loggedIn = this.authService.User.isLoggedIn;
        if (this.route.queryParams) {
            let paramsObs$ = this.route.queryParams.pipe(filter(p => p["notfound"] != null)).subscribe(
                (params: Params) => {
                    this.notFound = <boolean>params["notfound"];
                },
                (error) => { console.error(error); });
            this.subscription.add(paramsObs$);
        }

        this.updateProfile();
        this.getConfig();
        this.getAlerts();

        if (this.route.snapshot.url.length > 0 && this.route.snapshot.url[this.route.snapshot.url.length - 1].path === "login") {
            if (this.cookiesAccepted == false) this.showCookieDialog(false);
        }

    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    acceptCookie(accept: boolean): void {
        this.authService.cookieAcceptance = accept;
        if (this.doNavigate && accept) {
            this.executeLogin();
            
        }

    }

    acceptCookieAndRegister(accept: boolean): void {
        this.authService.cookieAcceptance = accept;
        if (accept) this.executeRegister();
    }

    executeLogin(): void {
        if (this.loginLink != null) (<HTMLAnchorElement>this.loginLink.nativeElement).click();
    }

    executeRegister(): void {
        if (this.registerLink != null) (<HTMLAnchorElement>this.registerLink.nativeElement).click();
    }

    getAlerts(): void {
        let alert$ = this.configService.getSystemAlerts().pipe(
            tap((alerts: SystemAlert[]) => {
                this.alerts = alerts;
            })
        ).subscribe(
            () => { },
            (error) => { console.error(error); }
        );
        this.subscription.add(alert$);
    }

    getConfig(): void {
        let conf$ = this.authService.getTimeoutConfig().subscribe(
            (conf: TimeoutConfig) => { this._config = conf; },
            (error) => { console.error(error); }
        );
        this.subscription.add(conf$);
    }

    promptCookiesBeforeLogin(): void {
        if (this.cookiesAccepted) {
            this.executeLogin();
        } else {
            this.doNavigate = true;
            this.showCookieDialog(false);
        }
    }

    promptCookiesBeforeRegistration(): void {
        if (this.cookiesAccepted) this.executeRegister();
        else {
            this.doNavigate = true;
            this.showCookieDialog(true);
        }
    }

    showCookieDialog(newUser: boolean): void {
        let dialog: Dialog = (newUser) ? this.newUserCookieDialog : this.cookieDialog;
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    updateProfile(): void {
        let profs$ = this.authService.setProfile().pipe().subscribe(
            (identity: UserIdentity) => {
                this._loggedIn = (identity) ? identity.isLoggedIn : false;
                if (identity.isAnonymous) {
                    console.log("anonymous profile set");
                    this._anonymousSet = true;
                } else {
                    console.log("somehow still have a non-anonymous profile on the server, log them out");
                    // this.authService.logout();
                    this.router.navigate(["auth", "logout"]);
                }

            },
            (error) => { console.error(error); }
        );
        this.subscription.add(profs$);
    }
}
